<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import FeedArticles from '@/components/common/feed-articles.vue';
import { Theme } from '@/types';
import { useIsNextLayout } from '@/utils/composables/useLayoutVariant';
import { ContentPosition, syncContentScrollWithViewport } from '@/utils/scroll';
import { useStore } from '@/utils/vue';
import RelatedNewsPreview from './related-news-preview.vue';

const props = defineProps<{
  theme: Theme;
}>();

const store = useStore();

const feedType = computed(() => store.state.feedType.feedType);

/**
 * 旧レイアウトはwindowスクロールに合わせてJSで追従位置を計算しているが、
 * 新レイアウトはcontent領域がスクロールコンテナなのでCSSのposition: stickyで足りる
 */
const isNextLayout = useIsNextLayout();

onMounted(async () => {
  if (isNextLayout) return;
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('resize', handleResize);
  handleScroll();
});

onBeforeUnmount(() => {
  if (isNextLayout) return;
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', handleResize);
});

const activityColumn = ref<HTMLElement | undefined>();
const activityContent = ref<HTMLElement | undefined>();
let prevWindowScrollY = 0;
let contentPosition: ContentPosition = 'top';
const TITLE_SECTION_HEIGHT = 36;
const CONTENT_HEADER_HEIGHT = 121 + TITLE_SECTION_HEIGHT; // 121（コンテンツヘッダーの高さ） + タイトルセクション高さ

const handleResize = (event?: Event) => handleScroll(event, true);
const handleScroll = (_event?: Event, reset = false) => {
  if (!activityColumn.value || !activityContent.value) return;
  const data = syncContentScrollWithViewport(
    activityContent.value,
    activityColumn.value,
    prevWindowScrollY,
    contentPosition,
    CONTENT_HEADER_HEIGHT,
    TITLE_SECTION_HEIGHT,
    reset,
  );
  if (data) {
    prevWindowScrollY = data.windowScrollY;
    contentPosition = data.contentPosition;
  }
};

const showRelatedNewsPreview = computed(() => {
  return (
    props.theme &&
    !props.theme.is_chinese &&
    (feedType.value !== 'foreign' || props.theme.is_foreign_media_enabled)
  );
});
</script>

<template>
  <div class="keyword-feed">
    <div class="m-feed-ready" :key="theme.id">
      <div class="title">
        <span class="subtitle c-text-regular--m">
          ニュースは毎日7:00に更新されます。
        </span>
      </div>
      <FeedArticles feed="keyword_feed" />
    </div>
    <div
      class="o-team-activity"
      ref="activityColumn"
      :class="{ 'is-next-layout': isNextLayout }"
    >
      <div
        class="o-team-activity-content"
        ref="activityContent"
        :style="isNextLayout ? undefined : { top: `${TITLE_SECTION_HEIGHT}px` }"
      >
        <div v-if="showRelatedNewsPreview" class="related-news">
          <RelatedNewsPreview :theme="theme"></RelatedNewsPreview>
          <div class="spacing-16"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
div.keyword-feed {
  display: flex;
  div.m-feed-ready {
    display: flex;
    flex-direction: column;
    width: 632px;

    .title {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: 16px;

      .subtitle {
        color: $color-gray600;
      }

      .feed-toggle {
        align-self: center;
        padding: 0 18px;
        cursor: pointer;
        color: $color-green600;
      }
    }

    div.m-no-related-feed {
      font-size: 12px;
    }
    .m-feed-menu {
      display: flex;
      justify-content: flex-end;
      .m-foreign-media-button {
        cursor: pointer;
        color: $color-green600;
        margin-right: 40px;
        padding: 8px 16px;
        &:hover {
          background: $color-gray400;
          border-radius: 4px;
        }
      }
    }
    div.m-feed-header {
      height: 24px;
      margin-bottom: 16px;

      span.m-feed-title {
        color: $color-gray1000;
        font-weight: bold;
      }

      span.m-feed-description {
        font-size: 12px;
        color: $color-gray600;
      }
    }
    .m-no-related-feed {
      display: flex;
      flex-direction: row;
      width: 616px;
      height: 120px;
      background: #ffffff;
      border: 1px solid $color-gray400;
      box-sizing: border-box;
      border-radius: 4px;
      align-items: center;
      justify-content: space-between;
      padding: 0 32px;
    }
    .m-no-related-feed {
      height: 120px;
      .m-description {
      }
      img {
        height: 100px;
        width: 100px;
      }
    }
  }
  .m-feed-removed {
    .m-body {
      width: 616px;
      height: 54px;
      background: white;
      display: flex;
      align-items: center;
      margin-top: 16px;
      .m-text {
        margin-left: 16px;
      }
    }
  }
  .loading {
    margin: 0 auto;
  }
  div.o-team-activity {
    position: relative;
    width: 280px;
    .o-team-activity-content {
      position: absolute;
      padding-bottom: 80px;
      .featured-articles {
        width: 280px;
      }
    }

    &.is-next-layout .o-team-activity-content {
      position: sticky;
      top: calc(var(--layout-sticky-top-under-tabs, #{$header-height}) + 24px);
      margin-top: 38px; // 左にあるsubtitleの22px + gapの16px
    }
  }
}
</style>
